/* eslint-disable @typescript-eslint/indent */
import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// SWA-5628 Temporary hide: Lender Pricing/Market Pricing toggle from RAC page with Market Pricing state by default.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { DollarAdornment, Toggle } from 'modules/core/components';
import { Input, Multiselect } from 'modules/core/components';
import { useSelector } from 'react-redux';
import { getSubventionYearOptions } from 'store/selectors/controls.selector';
import { IAvailableFilters, IField, IOption } from 'modules/core/types';
import { ColumnWidth, TableView } from 'modules/core/enums';
import { useFormContext } from 'react-hook-form';
import { getDealerStores } from 'store/selectors/dealer.selector';
import { getVehicleState } from 'store/selectors/vehicle.selector';
import { getUser } from 'store/selectors/auth.selector';
import { COMMA_SYMBOL } from 'modules/core/constants';
// SWA-5628 Temporary hide: Lender Pricing/Market Pricing toggle from RAC page with Market Pricing state by default.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { PermissionWrapper } from 'modules/PermissionWrapper';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { PERMISSIONS } from 'modules/PermissionWrapper/permissions.const';
import { getFeaturesList } from 'store/selectors/ui.selector';

import { MULTIPLY_VALUES_OPTIONS } from '../Multiselect/Multiselect.const';
import { VEHICLE_FILTERS_FIELDS } from './fields.const';
import {
  AGE_OPTIONS,
  ALL_OPTION,
  ALL_MODELS_OPTION,
  COLOR_CANONICAL_OPTIONS,
  DRIVE_TYPE_OPTIONS,
  EXTRA_CASH_OPTIONS,
  THIRD_ROW_SEAT_OPTIONS,
  VEHICLE_TYPE_OPTIONS,
} from './options.const';
// SWA-5628 Temporary hide: Lender Pricing/Market Pricing toggle from RAC page with Market Pricing state by default.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ToggleContainer } from './styles';
import { AdditionalComponent, FilterLabel, FilterSection, FilterCol } from './styles';
import {
  FILTER_FIELDS_SUBSCRIPTION,
  FILTER_OPTION_VALUES,
  FILTERS_FIELD_NAMES,
  TABLE_VIEW,
} from './vehicleFilters.const';
import { IVehicleFilterField } from './types';
import { getCurrentVehicleModelsFilterOptions } from '../../utils';
import { getEffectiveYearOptions, hasAnyAvailableValues } from './FilterSection.utils';
import { useProcessFilter } from './hooks/useProcessFilter';

interface IVehicleFilterSection {
  isRac?: boolean;
  isLender?: boolean;
  availableFilters?: IAvailableFilters;
}

function filterOptionsByAvailable(
  options: IOption[],
  availableValues: string[],
  selectedFieldValue: string | undefined,
  normalizer: (value: string) => string = (value) => value,
  alwaysIncludeValues: string[] = [],
): IOption[] {
  const availableSet = new Set(availableValues.map((value) => normalizer(String(value))));
  const selectedSet = new Set(
    (selectedFieldValue || '')
      .split(COMMA_SYMBOL)
      .map((v) => v.trim())
      .map((v) => normalizer(v))
      .filter(Boolean),
  );
  const alwaysIncludeSet = new Set(alwaysIncludeValues.map((v) => normalizer(v)));

  return options.filter((opt) => {
    const optionValue = String(opt.value ?? '');
    const normalizedValue = normalizer(optionValue);

    if (optionValue === '') return true;

    if (MULTIPLY_VALUES_OPTIONS.includes(optionValue)) return true;

    if (alwaysIncludeSet.has(normalizedValue)) return true;

    if (availableSet.has(normalizedValue)) return true;

    if (selectedSet.has(normalizedValue)) return true;

    return false;
  });
}

function filterMakeOptionsByAvailable(
  options: IOption[],
  availableValues: string[],
  selectedFieldValue: string | undefined,
): IOption[] {
  const availableSet = new Set(availableValues);
  const availableMakeTypes = new Set(
    options
      .filter((option) => availableSet.has(String(option.value)))
      .map((option) => (option as IOption & { make_type?: string }).make_type?.toLowerCase())
      .filter(Boolean),
  );
  const selectedSet = new Set(
    (selectedFieldValue || '')
      .split(COMMA_SYMBOL)
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean),
  );

  return filterOptionsByAvailable(options, availableValues, selectedFieldValue).filter((option) => {
    const optionValue = String(option.value ?? '');

    if (!MULTIPLY_VALUES_OPTIONS.includes(optionValue)) return true;

    const normalizedGroupValue = optionValue.toLowerCase();

    return selectedSet.has(normalizedGroupValue) || availableMakeTypes.has(normalizedGroupValue);
  });
}

function getAvailableValueOptions(
  allOption: IOption,
  availableValues: string[],
  selectedFieldValue?: string,
  sortDirection: 'asc' | 'desc' = 'asc',
): IOption[] {
  const allOptionSentinels = new Set([String(allOption.value ?? ''), allOption.title].filter(Boolean));
  const selectedValues = (selectedFieldValue || '')
    .split(COMMA_SYMBOL)
    .map((value) => value.trim())
    .filter((value) => value && !allOptionSentinels.has(value));
  const values = Array.from(new Set([...availableValues, ...selectedValues])).sort((a, b) =>
    sortDirection === 'desc' ? b.localeCompare(a) : a.localeCompare(b),
  );

  return [
    allOption,
    ...values.map((value) => ({
      title: value,
      value,
    })),
  ];
}

function getSelectedMultiselectValues(selectedFieldValue?: string): string[] {
  return (selectedFieldValue || '')
    .split(COMMA_SYMBOL)
    .map((value) => value.trim())
    .filter(Boolean);
}

function getAvailableDropdownOptions(
  options: IOption[],
  availableValues: string[],
  selectedFieldValue: string | undefined,
  normalizer: (value: string) => string = (value) => value,
): IOption[] {
  return filterOptionsByAvailable(options, availableValues, selectedFieldValue, normalizer, [String(ALL_OPTION.value)]);
}

export const VehicleFilterSection: FC<IVehicleFilterSection> = ({
  isRac = false,
  isLender = false,
  availableFilters: availableFiltersFromSearch,
}) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext();
  const user = useSelector(getUser);
  const isWholesaleInventory = user?.withWholesaleInventory;
  const dealerStores = useSelector(getDealerStores);
  const { vehicleBodyTypes, vehicleMakes, vehicleModels } = useSelector(getVehicleState);
  const featureList = useSelector(getFeaturesList);

  const subventionYearOptions = useSelector(getSubventionYearOptions);
  const { tableView } = watch();

  const availableFilters = useMemo(
    () => (hasAnyAvailableValues(availableFiltersFromSearch) ? availableFiltersFromSearch : undefined),
    [availableFiltersFromSearch],
  );

  const {
    [FILTERS_FIELD_NAMES.MILES]: miles,
    [FILTERS_FIELD_NAMES.YEARS]: years,
    [FILTERS_FIELD_NAMES.YEARS_VALUE]: selectedYearValue,
    [FILTERS_FIELD_NAMES.AGES]: selectedAges,
    [FILTERS_FIELD_NAMES.STORES]: selectedStores,
    [FILTERS_FIELD_NAMES.VEHICLE_TYPE]: selectedCondition,
    [FILTERS_FIELD_NAMES.VEHICLES]: selectedVehicles,
    [FILTERS_FIELD_NAMES.MAKES]: makes,
    [FILTERS_FIELD_NAMES.MODELS]: selectedModels,
    [FILTERS_FIELD_NAMES.THIRD_ROW_SEAT]: selectedThirdRowSeat,
    [FILTERS_FIELD_NAMES.DRIVE_TYPE]: selectedDriveTypes,
    [FILTERS_FIELD_NAMES.COLORS]: selectedColors,
    [FILTERS_FIELD_NAMES.EXTRA_CASH]: selectedExtraCash,
  } = watch([
    ...FILTER_FIELDS_SUBSCRIPTION,
    FILTERS_FIELD_NAMES.YEARS_VALUE,
    FILTERS_FIELD_NAMES.AGES,
    FILTERS_FIELD_NAMES.STORES,
    FILTERS_FIELD_NAMES.VEHICLE_TYPE,
    FILTERS_FIELD_NAMES.VEHICLES,
    FILTERS_FIELD_NAMES.THIRD_ROW_SEAT,
    FILTERS_FIELD_NAMES.DRIVE_TYPE,
    FILTERS_FIELD_NAMES.COLORS,
    FILTERS_FIELD_NAMES.EXTRA_CASH,
  ]);

  const currentModels = getCurrentVehicleModelsFilterOptions(makes, vehicleModels);
  const selectedMakeValues = useMemo(() => getSelectedMultiselectValues(makes), [makes]);
  const hasConcreteMakeSelection = useMemo(
    () => selectedMakeValues.some((value) => !MULTIPLY_VALUES_OPTIONS.includes(value) && value !== ALL_OPTION.value),
    [selectedMakeValues],
  );

  const effectiveMakes = useMemo(
    () =>
      availableFilters?.makes
        ? filterMakeOptionsByAvailable(vehicleMakes, availableFilters.makes, makes)
        : vehicleMakes,
    [vehicleMakes, availableFilters?.makes, makes],
  );

  const effectiveStores = useMemo(
    () =>
      availableFilters?.dealers
        ? filterOptionsByAvailable(dealerStores, availableFilters.dealers, selectedStores, (value) =>
            value.includes(':') ? value.slice(value.indexOf(':') + 1) : value,
          )
        : dealerStores,
    [dealerStores, availableFilters?.dealers, selectedStores],
  );

  const effectiveConditions = useMemo(
    () =>
      availableFilters?.conditions
        ? filterOptionsByAvailable(
            VEHICLE_TYPE_OPTIONS,
            availableFilters.conditions,
            selectedCondition,
            (value) => value.toLowerCase(),
            [FILTER_OPTION_VALUES.VEHICLE_TYPE.ALL],
          )
        : VEHICLE_TYPE_OPTIONS,
    [availableFilters?.conditions, selectedCondition],
  );

  const effectiveBodyTypes = useMemo(
    () =>
      availableFilters?.body_types
        ? filterOptionsByAvailable(vehicleBodyTypes, availableFilters.body_types, selectedVehicles)
        : vehicleBodyTypes,
    [vehicleBodyTypes, availableFilters?.body_types, selectedVehicles],
  );

  const effectiveModels = useMemo(() => {
    if (!availableFilters?.models) {
      return currentModels;
    }

    return getAvailableValueOptions(ALL_MODELS_OPTION, availableFilters.models, selectedModels);
  }, [currentModels, availableFilters?.models, selectedModels]);

  const effectiveThirdRowSeat = useMemo(
    () =>
      availableFilters?.third_row_seat
        ? filterOptionsByAvailable(
            THIRD_ROW_SEAT_OPTIONS,
            availableFilters.third_row_seat,
            selectedThirdRowSeat,
            (value) => value.toLowerCase(),
          )
        : THIRD_ROW_SEAT_OPTIONS,
    [availableFilters?.third_row_seat, selectedThirdRowSeat],
  );

  const effectiveDriveTypes = useMemo(
    () =>
      availableFilters?.drive_types
        ? filterOptionsByAvailable(DRIVE_TYPE_OPTIONS, availableFilters.drive_types, selectedDriveTypes)
        : DRIVE_TYPE_OPTIONS,
    [availableFilters?.drive_types, selectedDriveTypes],
  );

  const effectiveColors = useMemo(
    () =>
      availableFilters?.colors
        ? filterOptionsByAvailable(COLOR_CANONICAL_OPTIONS, availableFilters.colors, selectedColors)
        : COLOR_CANONICAL_OPTIONS,
    [availableFilters?.colors, selectedColors],
  );

  const effectiveYearValues = useMemo(
    () => getEffectiveYearOptions(subventionYearOptions, availableFilters?.years, selectedYearValue, years),
    [availableFilters?.years, selectedYearValue, subventionYearOptions, years],
  );
  const effectiveAges = useMemo(
    () =>
      availableFilters?.ages
        ? getAvailableDropdownOptions(AGE_OPTIONS, availableFilters.ages, selectedAges)
        : AGE_OPTIONS,
    [availableFilters?.ages, selectedAges],
  );

  const effectiveExtraCash = useMemo(
    () =>
      availableFilters?.extra_cash
        ? getAvailableDropdownOptions(EXTRA_CASH_OPTIONS, availableFilters.extra_cash, selectedExtraCash)
        : EXTRA_CASH_OPTIONS,
    [availableFilters?.extra_cash, selectedExtraCash],
  );

  const mapOptionsByField = useMemo(
    () =>
      new Map([
        [FILTERS_FIELD_NAMES.STORES, effectiveStores],
        [FILTERS_FIELD_NAMES.VEHICLE_TYPE, effectiveConditions],
        [FILTERS_FIELD_NAMES.VEHICLES, effectiveBodyTypes],
        [FILTERS_FIELD_NAMES.YEARS_VALUE, effectiveYearValues],
        [FILTERS_FIELD_NAMES.AGES, effectiveAges],
        [FILTERS_FIELD_NAMES.MAKES, effectiveMakes],
        [FILTERS_FIELD_NAMES.MODELS, effectiveModels],
        [FILTERS_FIELD_NAMES.THIRD_ROW_SEAT, effectiveThirdRowSeat],
        [FILTERS_FIELD_NAMES.DRIVE_TYPE, effectiveDriveTypes],
        [FILTERS_FIELD_NAMES.COLORS, effectiveColors],
        [FILTERS_FIELD_NAMES.EXTRA_CASH, effectiveExtraCash],
      ]),
    [
      effectiveStores,
      effectiveConditions,
      effectiveBodyTypes,
      effectiveYearValues,
      effectiveAges,
      effectiveMakes,
      effectiveModels,
      effectiveThirdRowSeat,
      effectiveDriveTypes,
      effectiveColors,
      effectiveExtraCash,
    ],
  );

  const additionalComponentDependencyMap = useMemo(
    () =>
      new Map<string, boolean>([
        [FILTERS_FIELD_NAMES.YEARS_VALUE, !!years],
        [FILTERS_FIELD_NAMES.ODOMETER_MILES, !!miles],
      ]),
    [years, miles],
  );

  const DISCOUNTING_LABELS_MAP = new Map<string, string>([
    [TableView.SALESPERSON, t('components.vehicleFilters.labels.marketPricing')],
    [TableView.MANAGER, t('components.vehicleFilters.labels.lenderPricing')],
  ]);

  // SWA-5628 Temporary hide: Lender Pricing/Market Pricing toggle from RAC page with Market Pricing state by default.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const discountingLabel = DISCOUNTING_LABELS_MAP.get(tableView) || t('components.vehicleFilters.labels.discounting');

  // SWA-5628 Temporary hide: Lender Pricing/Market Pricing toggle from RAC page with Market Pricing state by default.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onDiscountingChange = useCallback(
    (e) => {
      const tableViewValue = e.target.checked ? TABLE_VIEW.roles.manager : TABLE_VIEW.roles.salesperson;

      setValue(e.target.name, tableViewValue);
    },
    [setValue],
  );

  const renderAdditionalField = useCallback(
    ({ name, defaultValue, controlProps, component: Component }: IField) => {
      const shouldRender = additionalComponentDependencyMap.get(name) ?? true;

      return (
        <>
          {shouldRender && (
            <Component
              name={name}
              defaultValue={defaultValue as string}
              options={(mapOptionsByField.get(name) as IOption[]) || subventionYearOptions}
              {...controlProps}
            />
          )}
        </>
      );
    },
    [additionalComponentDependencyMap, mapOptionsByField, subventionYearOptions],
  );

  const getFields = useCallback(
    (fieldData) =>
      fieldData.map(({ name, label, controlProps, component: Component, additionalComponent }: IVehicleFilterField) => {
        const fieldOptions = mapOptionsByField.get(name) || (controlProps?.options as IOption[]) || [];

        return (
          <FilterCol key={name} xs={ColumnWidth.FULL} sm={ColumnWidth.XS} lg={ColumnWidth.XXXS}>
            <FilterLabel>{label}</FilterLabel>
            {name === FILTERS_FIELD_NAMES.STORES ||
            name === FILTERS_FIELD_NAMES.VEHICLES ||
            name === FILTERS_FIELD_NAMES.MAKES ||
            name === FILTERS_FIELD_NAMES.MODELS ? (
              <Multiselect
                name={name}
                options={fieldOptions}
                isDisabled={name === FILTERS_FIELD_NAMES.MODELS && !hasConcreteMakeSelection}
                hasDynamicModelOptions={name === FILTERS_FIELD_NAMES.MAKES && !!availableFilters?.models}
              />
            ) : (
              <Component name={name} options={fieldOptions} disabled={controlProps?.disabled as boolean} />
            )}
            {additionalComponent && (
              <AdditionalComponent>{renderAdditionalField(additionalComponent)}</AdditionalComponent>
            )}
          </FilterCol>
        );
      }),
    [availableFilters?.models, hasConcreteMakeSelection, mapOptionsByField, renderAdditionalField],
  );

  const isVehicleTypeDisabled = !!user?.isWithInventory && !featureList.can_fetch_new_vehicles;

  const vehicleTypeField = VEHICLE_FILTERS_FIELDS.find((field) => field.name === FILTERS_FIELD_NAMES.VEHICLE_TYPE);

  if (vehicleTypeField) {
    vehicleTypeField.controlProps = {
      ...vehicleTypeField.controlProps,
      disabled: isVehicleTypeDisabled,
    };
  }

  const filtersWithProcessExtraCash = useProcessFilter(
    VEHICLE_FILTERS_FIELDS,
    tableView === TABLE_VIEW.roles.manager,
    featureList.is_calculated_book_value,
    FILTERS_FIELD_NAMES.EXTRA_CASH,
  );

  const visibleFilters = isWholesaleInventory
    ? filtersWithProcessExtraCash.filter(
        (field: IVehicleFilterField) =>
          field.name !== FILTERS_FIELD_NAMES.THIRD_ROW_SEAT &&
          field.name !== FILTERS_FIELD_NAMES.STORES &&
          field.name !== FILTERS_FIELD_NAMES.AGES,
      )
    : filtersWithProcessExtraCash.filter((field: IVehicleFilterField) => field.name !== FILTERS_FIELD_NAMES.COLORS);

  const filtersFields = getFields(visibleFilters);

  return (
    <>
      <FilterSection>
        {filtersFields}
        <FilterCol xs={ColumnWidth.FULL} sm={ColumnWidth.XS} lg={ColumnWidth.XXXS}>
          <FilterLabel>{t('components.vehicleFilters.labels.minimumPayment')}</FilterLabel>
          <Input
            name={FILTERS_FIELD_NAMES.MINIMUM_MONTHLY_PAYMENT}
            placeholder={t('components.input.placeholders.integer')}
            decimalScale={0}
            startAdornment={<DollarAdornment />}
            hideInitZeroValue
          />
        </FilterCol>
        {/* SWA-5628 Temporary hide: Lender Pricing/Market Pricing toggle from RAC page with Market Pricing state by default. */}
        {/* SWA-6196 Temporary hide: Lender Pricing/Market Pricing toggle from Lender page with Market Pricing state by default. */}
        {!isRac && !isLender && (
          <PermissionWrapper allowedRoles={PERMISSIONS.DISCOUNTING_TOGGLE}>
            <FilterCol xs={ColumnWidth.FULL} sm={ColumnWidth.XS} lg={ColumnWidth.XXXS}>
              <FilterLabel>{discountingLabel}</FilterLabel>
              <ToggleContainer>
                <Toggle
                  icons={false}
                  name={TABLE_VIEW.name}
                  changeCallback={onDiscountingChange}
                  checked={tableView === TABLE_VIEW.roles.manager}
                />
              </ToggleContainer>
            </FilterCol>
          </PermissionWrapper>
        )}
      </FilterSection>
    </>
  );
};
